import { sectionRenderer } from '@theme/section-renderer';
import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

const IMAGE_VIEW_KEY = 'product-image-view';
/** Page size of the numbered pagination template. Load more uses the grid's own page size. */
const SIZE_STOCK_PAGE_SIZE = 40;

/**
 * In-stock product cards for one filtered collection, in collection order.
 * Keyed without the page so page changes reuse the same list.
 * @type {Map<string, { cards: Element[] }>}
 */
const sizeStockIndex = new Map();

/**
 * Lowercased size name for a filter value stored as an id.
 * @param {string} paramName
 * @param {string} gid
 * @returns {string}
 */
function groupedSizeLabel(paramName, gid) {
  if (typeof CSS === 'undefined' || typeof CSS.escape !== 'function') return '';

  const input = document.querySelector(
    `input[name="${CSS.escape(paramName)}"][data-filter-value="${CSS.escape(gid)}"], input[name="${CSS.escape(paramName)}"][value="${CSS.escape(gid)}"]`
  );
  if (!(input instanceof HTMLInputElement)) return '';

  return (input.dataset.label || input.getAttribute('aria-label') || '').trim().toLowerCase();
}

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #sizeStockGen = 0;
  /** @type {Promise<boolean> | null} */
  #sizeStockPromise = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onSizeStockClick, true);
    window.addEventListener('popstate', this.#onSizeStockPop);

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.addEventListener('paginated-list:new-elements', this.#applyImageViewToNewElements);
    this.setAttribute('initialized', '');
    const ghostSwitcherEnabled = this.getAttribute('data-ghost-switcher-enabled') !== 'false';
    const storedImageView = ghostSwitcherEnabled
      ? (sessionStorage.getItem(IMAGE_VIEW_KEY) || 'model')
      : 'model';
    if (!ghostSwitcherEnabled) sessionStorage.setItem(IMAGE_VIEW_KEY, 'model');
    requestIdleCallback(() => {
      this.#setImageView(storedImageView);
      const radio = this.querySelector(
        `input[type="radio"][name="product-image-view"][value="${storedImageView}"]`
      );
      if (radio instanceof HTMLInputElement) radio.checked = true;
      const toggle = this.querySelector('input[data-image-view-toggle]');
      if (toggle instanceof HTMLInputElement) toggle.checked = storedImageView === 'ghost';
      this.#applySizeStockFilter();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onSizeStockClick, true);
    window.removeEventListener('popstate', this.#onSizeStockPop);
    this.#sizeStockGen += 1;
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.removeEventListener('paginated-list:new-elements', this.#applyImageViewToNewElements);
  }

  /** Re-apply current image view (model/ghost) when load more adds new cards. Only processes new elements to avoid O(n) over whole grid. */
  #applyImageViewToNewElements = (event) => {
    const ghostSwitcherEnabled = this.getAttribute('data-ghost-switcher-enabled') !== 'false';
    const value = ghostSwitcherEnabled ? (sessionStorage.getItem(IMAGE_VIEW_KEY) || 'model') : 'model';
    const newElements = event?.detail?.elements;
    const scope = Array.isArray(newElements) && newElements.length > 0 ? newElements : null;
    this.#setImageView(value, scope);
    requestAnimationFrame(() => {
      this.#setImageView(value, scope);
      setTimeout(() => this.#setImageView(value, scope), 50);
    });
    this.#applySizeStockFilter();
  };

  updatedCallback() {
    super.updatedCallback();
    const value = sessionStorage.getItem(IMAGE_VIEW_KEY) || 'model';
    requestIdleCallback(() => {
      this.#setImageView(value);
      this.#applySizeStockFilter();
    });
  }

  /**
   * Updates the image view (Model / Ghost). Called from image-view-controls (radio variant).
   * @param {Event} event - change event from radio input
   */
  updateImageView({ target }) {
    if (!(target instanceof HTMLInputElement) || !target.value) return;
    const value = target.value === 'ghost' ? 'ghost' : 'model';
    sessionStorage.setItem(IMAGE_VIEW_KEY, value);
    this.#setImageView(value);
    this.#pushImageViewEvent(value);
  }

  /**
   * Updates the image view from the Ghost toggle (checkbox). Checked = ghost, unchecked = model.
   * @param {Event} event - change event from checkbox
   */
  updateImageViewFromToggle({ target }) {
    if (!(target instanceof HTMLInputElement)) return;
    const value = target.checked ? 'ghost' : 'model';
    sessionStorage.setItem(IMAGE_VIEW_KEY, value);
    this.#setImageView(value);
    this.#pushImageViewEvent(value);
  }

  /**
   * Pushes image_view_change to GA (gtag) and/or GTM (dataLayer).
   * @param {string} view - 'model' | 'ghost'
   */
  #pushImageViewEvent(view) {
    const params = { image_view: view };
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'image_view_change', params);
    }
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: 'image_view_change', ...params });
    }
  }

  /**
   * Sets product-image-view on grid and swaps card images (model vs ghost).
   * Uses the first slide (card cover) so video covers are handled reliably.
   * @param {string} value - 'model' | 'ghost'
   * @param {Element[] | null} [scopeElements] - If set, only process galleries inside these elements (e.g. newly loaded cards); otherwise processes all.
   */
  #setImageView(value, scopeElements = null) {
    const grid = this.refs?.grid ?? this.querySelector('[ref="grid"]');
    if (grid) grid.setAttribute('product-image-view', value);
    const galleries =
      scopeElements?.length > 0
        ? scopeElements.flatMap((el) => Array.from(el.querySelectorAll('.card-gallery[data-ghost-src]')))
        : Array.from(this.querySelectorAll('.card-gallery[data-ghost-src]'));
    const useGhost = value === 'ghost';
    galleries.forEach((gallery) => {
      const firstSlide = gallery.querySelector('slideshow-slide');
      const container = firstSlide ?? gallery;
      const video = container.querySelector('video');
      const modelSrc = gallery.dataset.modelSrc;
      const ghostSrc = gallery.dataset.ghostSrc;

      if (video && ghostSrc) {
        this.#setImageViewForVideoCover(video, ghostSrc, useGhost);
      } else {
        const img = container.querySelector('img');
        if (img && !img.closest('video')) {
          this.#setImageViewForImage(img, gallery, modelSrc, ghostSrc, useGhost);
        }
      }
    });
  }

  /**
   * Swaps a single card's image (model vs ghost). Clears srcset when showing ghost.
   */
  #setImageViewForImage(img, gallery, modelSrc, ghostSrc, useGhost) {
    if (useGhost && ghostSrc) {
      if (!img.dataset.originalSrc) {
        img.dataset.originalSrc = img.currentSrc || img.getAttribute('src') || modelSrc || '';
        const srcset = img.getAttribute('srcset');
        if (srcset) img.dataset.originalSrcset = srcset;
      }
      img.setAttribute('src', ghostSrc);
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
    } else {
      const restoreSrc = img.dataset.originalSrc || modelSrc || img.getAttribute('src');
      img.setAttribute('src', restoreSrc);
      if (img.dataset.originalSrcset) {
        img.setAttribute('srcset', img.dataset.originalSrcset);
        delete img.dataset.originalSrcset;
      }
      delete img.dataset.originalSrc;
    }
  }

  /**
   * When the card cover is a video: in ghost mode show ghost image and hide video; in model mode show video and hide ghost img.
   */
  #setImageViewForVideoCover(video, ghostSrc, useGhost) {
    const mediaContainer = video.closest('.product-media') ?? video.parentElement;
    if (!mediaContainer) return;

    let ghostImg = mediaContainer.querySelector('.card-gallery__ghost-overlay');
    if (useGhost && ghostSrc) {
      if (!ghostImg) {
        ghostImg = document.createElement('img');
        ghostImg.src = ghostSrc;
        ghostImg.alt = '';
        ghostImg.loading = 'lazy';
        ghostImg.decoding = 'async';
        ghostImg.className = 'product-media__image card-gallery__ghost-overlay';
        ghostImg.setAttribute('data-ghost-overlay', '');
        mediaContainer.style.position = 'relative';
        mediaContainer.prepend(ghostImg);
      } else {
        ghostImg.src = ghostSrc;
        ghostImg.hidden = false;
        ghostImg.style.display = '';
      }
      video.hidden = true;
      video.style.setProperty('display', 'none', 'important');
    } else {
      if (ghostImg) {
        ghostImg.hidden = true;
        ghostImg.style.display = 'none';
      }
      video.hidden = false;
      video.style.removeProperty('display');
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    try {
      await startViewTransition(() => this.#setLayout(value), ['product-grid']);
    } catch (_e) {
      // If View Transitions fail (unsupported browser / script interference), still apply the layout.
      this.#setLayout(value);
    }

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Reads the active size filters from the URL. Shopify's size facet is variant
   * option based (e.g. `filter.v.option.Size=M`), so it matches any product that
   * *has* an M variant — even when M is sold out. Shopify has no variant-level
   * availability filter, so we use these values to hide products whose selected
   * size isn't in stock (checked against each card's size strip in the DOM).
   * @returns {{ sizes: Set<string>, hasGroupedValue: boolean }} resolvable (trimmed, lowercased)
   *   size values, plus a flag when a grouped/GID value is present that can't be matched here
   */
  #getActiveSizeFilters() {
    const sizes = new Set();
    let hasGroupedValue = false;
    try {
      for (const [key, value] of new URLSearchParams(window.location.search).entries()) {
        const normalizedKey = key.toLowerCase();
        if (!normalizedKey.startsWith('filter.') || !normalizedKey.includes('size')) continue;
        const normalizedValue = value.trim().toLowerCase();
        if (!normalizedValue) continue;
        // A size value that is still an id is matched by its checkbox label so the query
        // uses the name. Only an id with no label is left unresolved.
        if (normalizedValue.startsWith('gid:')) {
          const label = groupedSizeLabel(key, value);
          if (label) {
            sizes.add(label);
            continue;
          }
          hasGroupedValue = true;
          continue;
        }
        sizes.add(normalizedValue);
      }
    } catch (_error) {
      /* malformed URL — treat as no active size filter */
    }
    return { sizes, hasGroupedValue };
  }

  /**
   * Shopify's size filter counts every product that has the size, including sold-out
   * sizes, and paginates that larger set. Hiding the sold-out cards afterwards leaves
   * blank pages and a results total that no longer matches the filter. This rebuilds
   * each page from products whose selected size is in stock, and writes that total
   * onto both the results count and the size filter count.
   */
  #applySizeStockFilter() {
    const grid = this.refs?.grid ?? this.querySelector('[ref="grid"]');
    if (!(grid instanceof HTMLElement)) return;

    const items = Array.from(grid.querySelectorAll(':scope > .product-grid__item'));
    const { sizes, hasGroupedValue } = this.#getActiveSizeFilters();

    // A grouped value with no size name (for example "Other") can't be matched to a
    // size button. Leave the server page alone.
    if (hasGroupedValue || sizes.size === 0) {
      items.forEach((item) => this.#setSizeFilteredOut(item, false));
      delete this.dataset.sizeStockKey;
      delete this.dataset.sizeStockFailed;
      return;
    }

    const key = this.#sizeStockKey();
    if (this.dataset.sizeStockFailed === key) return;
    const cached = sizeStockIndex.get(key);
    if (cached) {
      if (this.dataset.sizeStockKey !== key) this.#paintSizeStock(cached, key);
      return;
    }

    // Hide sold-out cards immediately when the page would still show something.
    // A page that would go blank stays as the server rendered it until the
    // in-stock list is ready to replace it.
    let hidden = 0;
    items.forEach((item) => {
      const out = !this.#selectedSizeInStock(item, sizes);
      if (out) hidden += 1;
      this.#setSizeFilteredOut(item, out);
    });
    if (items.length > 0 && hidden === items.length) {
      items.forEach((item) => this.#setSizeFilteredOut(item, false));
    }

    this.#ensureSizeStockIndex(key, sizes);
  }

  /**
   * @param {Element} item
   * @param {Set<string>} sizes
   * @returns {boolean}
   */
  #selectedSizeInStock(item, sizes) {
    const strip = item.querySelector('.size-quick-add');
    if (!strip) return true;

    for (const button of strip.querySelectorAll('.size-quick-add__button')) {
      const label = (button.querySelector('.size-quick-add__label')?.textContent || '')
        .trim()
        .toLowerCase();
      if (!label || !sizes.has(label)) continue;
      const soldOut =
        button.classList.contains('size-quick-add__button--unavailable') ||
        button.hasAttribute('disabled');
      if (!soldOut) return true;
    }
    return false;
  }

  /** Filters without the page, so every page of this result set shares one index. */
  #sizeStockKey() {
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.delete('view');
    params.delete('lmps');
    params.delete('_su_rec');
    params.delete('_su_rec_id');
    const parts = [];
    for (const [key, value] of [...params.entries()].sort((a, b) => a[0].localeCompare(b[0]) || a[1].localeCompare(b[1]))) {
      if (key.startsWith('filter.') || key === 'sort_by' || key === 'preview_theme_id') {
        parts.push(`${key}=${value}`);
      }
    }
    return `${window.location.pathname}?${parts.join('&')}`;
  }

  /**
   * @param {string} key
   * @param {Set<string>} sizes
   */
  #ensureSizeStockIndex(key, sizes) {
    if (this.#sizeStockPromise || sizeStockIndex.has(key)) return;
    const gen = ++this.#sizeStockGen;
    this.#sizeStockPromise = this.#buildSizeStockIndex(key, sizes, gen)
      .then((ok) => {
        if (!ok && gen === this.#sizeStockGen && this.isConnected) {
          this.dataset.sizeStockFailed = key;
        }
        return ok;
      })
      .catch((error) => {
        console.warn('[ResultsList] In-stock page index failed', error);
        if (gen === this.#sizeStockGen && this.isConnected) this.dataset.sizeStockFailed = key;
        return false;
      })
      .finally(() => {
        this.#sizeStockPromise = null;
      });
  }

  /**
   * Walk every Shopify page of this filter. Availability does not remove products
   * whose selected size is sold out, so the in-stock list has to be built here.
   * @param {string} key
   * @param {Set<string>} sizes
   * @param {number} gen
   * @returns {Promise<boolean>}
   */
  async #buildSizeStockIndex(key, sizes, gen) {
    const grid = this.refs?.grid ?? this.querySelector('[ref="grid"]');
    if (!(grid instanceof HTMLElement)) return false;
    const serverTotal = parseInt(grid.getAttribute('total-items') || '0', 10);
    const sectionId = (this.getAttribute('section-id') || '').trim();
    if (!sectionId || serverTotal < 1) return false;

    const pages = Math.max(1, Math.ceil(serverTotal / SIZE_STOCK_PAGE_SIZE));
    /** @type {(Element[] | undefined)[]} */
    const cardPages = new Array(pages);
    let cursor = 0;

    const worker = async () => {
      while (cursor < pages) {
        const index = cursor;
        cursor += 1;
        const url = new URL(window.location.href);
        url.searchParams.set('page', String(index + 1));
        url.searchParams.set('view', 'pagination');
        url.searchParams.set('lmps', grid.getAttribute('data-page-size') || '20');
        url.searchParams.delete('_su_rec');
        url.searchParams.delete('_su_rec_id');
        url.hash = '';
        const html = await sectionRenderer.getSectionHTML(sectionId, false, url);
        if (typeof html !== 'string' || !html.includes('product-grid')) {
          cardPages[index] = undefined;
          return;
        }
        cardPages[index] = this.#inStockCardsFromHtml(html, sizes);
        await new Promise((resolve) => setTimeout(resolve, 0));
      }
    };

    await Promise.all(Array.from({ length: Math.min(4, pages) }, () => worker()));
    if (gen !== this.#sizeStockGen || !this.isConnected) return false;
    if (cardPages.some((page) => page == null)) return false;

    const seen = new Set();
    /** @type {Element[]} */
    const cards = [];
    for (const page of cardPages) {
      for (const card of page || []) {
        const id = card.getAttribute('data-product-id') || '';
        if (id && seen.has(id)) continue;
        if (id) seen.add(id);
        cards.push(card);
      }
    }

    while (sizeStockIndex.size > 3) {
      const oldest = sizeStockIndex.keys().next().value;
      if (oldest == null) break;
      sizeStockIndex.delete(oldest);
    }
    const entry = { cards };
    sizeStockIndex.set(key, entry);
    if (gen !== this.#sizeStockGen || !this.isConnected) return false;
    this.#paintSizeStock(entry, key);
    return true;
  }

  /**
   * @param {string} html
   * @param {Set<string>} sizes
   * @returns {Element[]}
   */
  #inStockCardsFromHtml(html, sizes) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    /** @type {Element[]} */
    const cards = [];
    doc.querySelectorAll('.product-grid__item').forEach((item) => {
      if (!this.#selectedSizeInStock(item, sizes)) return;
      const copy = item.cloneNode(true);
      if (copy instanceof Element) cards.push(copy);
    });
    return cards;
  }

  /**
   * @param {number} total
   * @param {number} pageSize
   * @param {number} limit
   */
  #lastLogicalPage(total, pageSize, limit) {
    const size = pageSize > 0 ? pageSize : 20;
    if (total < 1) return 1;
    if (limit <= 0) return Math.max(1, Math.ceil(total / size));
    const firstPortion = limit * size;
    if (total <= firstPortion) return Math.max(1, Math.ceil(total / size));
    return limit + Math.ceil((total - firstPortion) / SIZE_STOCK_PAGE_SIZE);
  }

  /**
   * @param {number} logicalPage
   * @param {number} pageSize
   * @param {number} limit
   */
  #sliceBounds(logicalPage, pageSize, limit) {
    const page = Math.max(1, logicalPage);
    if (limit <= 0 || page <= limit) {
      return { start: (page - 1) * pageSize, size: pageSize };
    }
    return {
      start: pageSize * limit + (page - limit - 1) * SIZE_STOCK_PAGE_SIZE,
      size: SIZE_STOCK_PAGE_SIZE,
    };
  }

  /**
   * @param {number} pageSize
   * @param {number} limit
   */
  #logicalPageFromUrl(pageSize, limit) {
    const params = new URLSearchParams(window.location.search);
    const raw = parseInt(params.get('page') || '1', 10);
    const page = Number.isFinite(raw) && raw >= 1 ? raw : 1;
    if (limit > 0 && params.get('view') === 'pagination') {
      const covered = Math.floor((limit * pageSize) / SIZE_STOCK_PAGE_SIZE);
      return limit + page - covered;
    }
    return page;
  }

  /**
   * @param {number} logicalPage
   * @param {number} pageSize
   * @param {number} limit
   */
  #sizeStockPageUrl(logicalPage, pageSize, limit) {
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.delete('_su_rec');
    url.searchParams.delete('_su_rec_id');
    if (logicalPage <= 1) {
      url.searchParams.delete('page');
      url.searchParams.delete('view');
      url.searchParams.delete('lmps');
    } else if (limit > 0 && logicalPage >= limit + 1) {
      const covered = Math.floor((limit * pageSize) / SIZE_STOCK_PAGE_SIZE);
      url.searchParams.set('page', String(logicalPage - limit + covered));
      url.searchParams.set('view', 'pagination');
      url.searchParams.set('lmps', String(pageSize));
    } else {
      url.searchParams.set('page', String(logicalPage));
      url.searchParams.delete('view');
      url.searchParams.delete('lmps');
    }
    return url;
  }

  /**
   * @param {{ cards: Element[] }} entry
   * @param {string} key
   * @param {number | null} [logicalOverride]
   * @param {{ push?: boolean, scroll?: boolean }} [options]
   */
  #paintSizeStock(entry, key, logicalOverride = null, options = {}) {
    const grid = this.refs?.grid ?? this.querySelector('[ref="grid"]');
    if (!(grid instanceof HTMLElement)) return;

    const pageSize = Number(grid.getAttribute('data-page-size')) || 20;
    const limit = Math.max(0, Number(grid.getAttribute('data-load-more-page-limit')) || 0);
    const last = this.#lastLogicalPage(entry.cards.length, pageSize, limit);
    let logical = logicalOverride ?? this.#logicalPageFromUrl(pageSize, limit);
    const clamped = logical > last;
    if (logical > last) logical = last;
    if (logical < 1) logical = 1;

    const { start, size } = this.#sliceBounds(logical, pageSize, limit);
    const slice = entry.cards.slice(start, start + size).map((node) => {
      const copy = node.cloneNode(true);
      if (copy instanceof Element) copy.setAttribute('data-page', String(logical));
      return copy;
    });
    grid.replaceChildren(...slice.filter((node) => node instanceof Element));
    this.dataset.sizeStockKey = key;
    this.#publishSizeStock(grid, entry.cards.length, pageSize, limit, logical, slice);

    const url = this.#sizeStockPageUrl(logical, pageSize, limit);
    if (options.push) {
      history.pushState({ sizeStockPage: logical }, '', url);
    } else if (clamped && url.href !== window.location.href) {
      history.replaceState({ sizeStockPage: logical }, '', url);
    }
    if (options.scroll) grid.scrollIntoView({ block: 'start' });
  }

  /**
   * @param {{ cards: Element[] }} entry
   * @param {string} key
   */
  #appendSizeStockPage(entry, key) {
    const grid = this.refs?.grid ?? this.querySelector('[ref="grid"]');
    if (!(grid instanceof HTMLElement)) return;

    const pageSize = Number(grid.getAttribute('data-page-size')) || 20;
    const limit = Math.max(0, Number(grid.getAttribute('data-load-more-page-limit')) || 0);
    const current = Number(grid.lastElementChild?.getAttribute('data-page') || '1');
    const last = this.#lastLogicalPage(entry.cards.length, pageSize, limit);
    if (current >= last) return;

    const next = current + 1;
    if (limit > 0 && current >= limit) {
      this.#paintSizeStock(entry, key, next, { push: true, scroll: true });
      return;
    }

    const { start, size } = this.#sliceBounds(next, pageSize, limit);
    const slice = entry.cards.slice(start, start + size).flatMap((node) => {
      const copy = node.cloneNode(true);
      if (!(copy instanceof Element)) return [];
      copy.setAttribute('data-page', String(next));
      return [copy];
    });
    if (slice.length === 0) return;
    grid.append(...slice);
    this.dataset.sizeStockKey = key;
    this.#publishSizeStock(grid, entry.cards.length, pageSize, limit, next, slice);
    history.pushState({ sizeStockPage: next }, '', this.#sizeStockPageUrl(next, pageSize, limit));
  }

  /**
   * @param {HTMLElement} grid
   * @param {number} total
   * @param {number} pageSize
   * @param {number} limit
   * @param {number} logical
   * @param {Node[]} slice
   */
  #publishSizeStock(grid, total, pageSize, limit, logical, slice) {
    const last = this.#lastLogicalPage(total, pageSize, limit);
    grid.setAttribute('total-items', String(total));
    grid.setAttribute('last-page', String(last));

    const shown = grid.querySelectorAll(':scope > .product-grid__item').length;
    if (logical < last && (limit === 0 || logical < limit)) {
      this.querySelector('.load-more-button')?.classList.remove('hidden');
      this.querySelector('.load-more-wrapper')?.classList.remove('hidden');
    }
    this.applyPagingChrome(logical, shown);

    document.querySelectorAll('[data-product-count]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      if (el.dataset.countOriginal == null) el.dataset.countOriginal = el.textContent || '';
      el.textContent = (el.dataset.countOriginal || '').replace(/\d[\d,]*/, String(total));
    });
    this.querySelectorAll('.load-more-text').forEach((el) => {
      el.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE && /\d/.test(node.textContent || '')) {
          node.textContent = (node.textContent || '').replace(/\d[\d,]*(?=\s*items)/, String(total));
        }
      });
    });

    const { sizes } = this.#getActiveSizeFilters();
    if (sizes.size === 1) {
      document.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;
        if (!input.name.toLowerCase().includes('size')) return;
        const count = input.closest('li')?.querySelector('.facets__count');
        if (count) count.textContent = String(total);
      });
    }

    const ghostSwitcherEnabled = this.getAttribute('data-ghost-switcher-enabled') !== 'false';
    const view = ghostSwitcherEnabled ? sessionStorage.getItem(IMAGE_VIEW_KEY) || 'model' : 'model';
    const elements = slice.filter((node) => node instanceof Element);
    this.#setImageView(view, elements);
  }

  /** Page numbers and Load more stay on the in-stock list instead of Shopify's pages. */
  #onSizeStockClick = (event) => {
    const { sizes, hasGroupedValue } = this.#getActiveSizeFilters();
    const key = this.#sizeStockKey();
    if (hasGroupedValue || sizes.size === 0 || this.dataset.sizeStockFailed === key) return;

    const target = event.target;
    if (!(target instanceof Element)) return;
    const loadMore = target.closest('.load-more-button');
    const pageLink = target.closest('.load-more-pagination a[href]');
    if (!(loadMore instanceof HTMLElement) && !(pageLink instanceof HTMLAnchorElement)) return;

    event.preventDefault();
    event.stopPropagation();

    const run = () => {
      if (!this.isConnected) return;
      if (this.dataset.sizeStockFailed === key) {
        if (loadMore instanceof HTMLElement) loadMore.click();
        else if (pageLink instanceof HTMLAnchorElement) window.location.assign(pageLink.href);
        return;
      }
      const entry = sizeStockIndex.get(key);
      if (!entry) return;
      if (loadMore instanceof HTMLElement) this.#appendSizeStockPage(entry, key);
      else if (pageLink instanceof HTMLAnchorElement) this.#openSizeStockLink(pageLink, entry, key);
    };

    if (sizeStockIndex.has(key)) {
      run();
      return;
    }
    this.#ensureSizeStockIndex(key, sizes);
    if (sizeStockIndex.has(key)) run();
    else this.#sizeStockPromise?.then(run);
  };

  /**
   * @param {HTMLAnchorElement} link
   * @param {{ cards: Element[] }} entry
   * @param {string} key
   */
  #openSizeStockLink(link, entry, key) {
    const grid = this.refs?.grid ?? this.querySelector('[ref="grid"]');
    if (!(grid instanceof HTMLElement)) return;
    const pageSize = Number(grid.getAttribute('data-page-size')) || 20;
    const limit = Math.max(0, Number(grid.getAttribute('data-load-more-page-limit')) || 0);
    const nav = link.closest('.load-more-pagination');
    const li = link.closest('[data-page]');
    let logical = li ? parseInt(li.getAttribute('data-page') || '', 10) : NaN;

    if (!Number.isFinite(logical) || logical < 1) {
      const currentLi = nav?.querySelector('.pagination__link--current')?.closest('[data-page]');
      const current = currentLi ? parseInt(currentLi.getAttribute('data-page') || '1', 10) : 1;
      if (link.hasAttribute('data-pagination-prev') || link.closest('[data-pagination-prev]')) {
        logical = Math.max(current - 1, 1);
      } else if (link.hasAttribute('data-pagination-next') || link.closest('[data-pagination-next]')) {
        logical = current + 1;
      } else {
        return;
      }
    }

    this.#paintSizeStock(entry, key, logical, { push: true, scroll: true });
  }

  #onSizeStockPop = () => {
    const { sizes, hasGroupedValue } = this.#getActiveSizeFilters();
    if (hasGroupedValue || sizes.size === 0) return;
    const key = this.#sizeStockKey();
    const entry = sizeStockIndex.get(key);
    if (!entry || !this.isConnected) return;
    this.#paintSizeStock(entry, key);
  };

  /**
   * Toggles the "hidden because the selected size is sold out" state on a grid item.
   * @param {Element} item - the `.product-grid__item` wrapper
   * @param {boolean} hidden
   */
  #setSizeFilteredOut(item, hidden) {
    if (!(item instanceof HTMLElement)) return;
    if (hidden) {
      item.classList.add('is-size-filtered-out');
      item.setAttribute('hidden', '');
    } else if (item.classList.contains('is-size-filtered-out')) {
      item.classList.remove('is-size-filtered-out');
      item.removeAttribute('hidden');
    }
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const viewport = event.matches ? 'desktop' : 'mobile';
    const storedLayout = sessionStorage.getItem(`product-grid-view-${viewport}`) || 'default';
    const inputName = viewport === 'desktop' ? 'grid' : 'grid-mobile';
    const targetElement = this.querySelector(
      `input[type="radio"][name="${inputName}"][value="${storedLayout}"]`
    );

    if (targetElement instanceof HTMLInputElement) {
      targetElement.checked = true;
      this.#setLayout(storedLayout);
      return;
    }

    // Fallback: if no matching option exists for this viewport, reset to default.
    const fallback = this.querySelector(
      viewport === 'desktop' ? '[data-grid-layout="desktop-default-option"]' : '[data-grid-layout="mobile-option"]'
    );
    if (fallback instanceof HTMLInputElement) {
      fallback.checked = true;
    }
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
